import { useCallback, useEffect, useRef, useState } from 'react';
import { getAudioPlayer, getPlayerMode } from 'app/store/AudioPlayer/selectors';
import { useAppDispatch, useAppSelector } from 'app/store';
import { setRepeatAction } from 'app/store/AudioPlayer/slice';
import { useAudioPlayer } from '../../hooks';
import { usePlayerEvents, WaveSurferInstance, PlaybackEngine } from '../../lib';
import { AudioPlayerControlsComponent } from './AudioPlayerControlsComponent';

export interface AudioPlayerControlsProps {
  isLoading: boolean;
  isPlaying: boolean;
  src?: string;
  isError?: boolean;
  isPlaylistMode: boolean;
  isOpen: boolean;
  setIsPlayerReady: (isReady: boolean) => void;
  setIsPlaying: (isPlaying: boolean) => void;
  isPlayerExpandable: boolean;
  playbackEngine: PlaybackEngine;
}

const SKIP_BACK_THRESHOLD_SEC = 3;

export const AudioPlayerControls = ({
  isLoading,
  isPlaying,
  src,
  isError,
  isPlaylistMode,
  isOpen,
  setIsPlayerReady,
  setIsPlaying,
  isPlayerExpandable,
  playbackEngine,
}: AudioPlayerControlsProps) => {
  const dispatch = useAppDispatch();
  const { repeat } = useAppSelector(getAudioPlayer);
  const [loop, setLoop] = useState(repeat);
  const { skip, skipBack, playPauseAudio } = useAudioPlayer();
  const { queue } = useAppSelector(getAudioPlayer);
  const playMode = useAppSelector(getPlayerMode);

  const timerRef = useRef<NodeJS.Timeout>();
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);

  useEffect(() => {
    setDuration(playbackEngine.getDuration());
  }, [playbackEngine, isLoading]);

  useEffect(() => {
    clearInterval(timerRef.current);
    if (isPlaying && isOpen) {
      timerRef.current = setInterval(() => {
        setCurrentTime(playbackEngine.getCurrentTime());
      }, 250);
    }
    return () => clearInterval(timerRef.current);
  }, [playbackEngine, isPlaying, isOpen]);

  useEffect(() => {
    if (isLoading) setCurrentTime(0);
  }, [isLoading]);

  const onFinish = useCallback(() => {
    if (!loop && playMode === 'playlist') {
      skip();
      return;
    }
    playbackEngine.seekTo(0);
    if (loop) {
      playbackEngine.play();
      setIsPlaying(true);
    } else {
      playbackEngine.stop();
      setIsPlaying(false);
    }
  }, [playbackEngine, loop, playMode, skip, setIsPlaying]);

  useEffect(() => {
    if (playbackEngine.type === 'wavesurfer') {
      window.player?.on('finish', onFinish);
      return () => {
        window.player?.un('finish', onFinish);
      };
    }
    if (!isPlaying) return undefined;
    const check = setInterval(() => {
      const d = playbackEngine.getDuration();
      if (d > 0 && playbackEngine.getCurrentTime() >= d - 0.1) {
        clearInterval(check);
        onFinish();
      }
    }, 200);
    return () => clearInterval(check);
  }, [playbackEngine, isPlaying, onFinish]);

  const wavesurferPlayer: WaveSurferInstance | undefined =
    playbackEngine.type === 'wavesurfer'
      ? window.player ?? undefined
      : undefined;
  usePlayerEvents(
    wavesurferPlayer,
    { isOpen, setIsPlayerReady },
    setCurrentTime
  );

  const shouldSkipBack =
    isPlaylistMode && !loop && currentTime < SKIP_BACK_THRESHOLD_SEC;

  const handleRewind = useCallback(() => {
    if (shouldSkipBack) skipBack();
    playbackEngine.seekTo(0);
    setCurrentTime(0);
  }, [playbackEngine, shouldSkipBack, skipBack]);

  const toggleLoop = useCallback(() => {
    dispatch(setRepeatAction(!loop));
    setLoop(!loop);
  }, [dispatch, loop]);

  const handleSeek = useCallback(
    (progress: number) => {
      playbackEngine.seekTo(progress);
      setCurrentTime(progress * duration);
    },
    [playbackEngine, duration]
  );

  const handleSkip = useCallback(() => {
    if (loop) {
      playbackEngine.seekTo(0);
      setCurrentTime(0);
      return;
    }
    skip();
  }, [loop, playbackEngine, skip]);

  useEffect(() => {
    navigator.mediaSession?.setActionHandler('play', () => {
      setIsPlaying(true);
      playbackEngine.play();
    });
    navigator.mediaSession?.setActionHandler('pause', () => {
      setIsPlaying(false);
      playbackEngine.pause();
    });
    navigator.mediaSession?.setActionHandler('previoustrack', () =>
      handleRewind()
    );
    navigator.mediaSession?.setActionHandler('nexttrack', () => handleSkip());
  }, [playbackEngine, setIsPlaying, handleRewind, handleSkip]);

  useEffect(() => {
    if (isPlaying) {
      playbackEngine.play();
    } else {
      playbackEngine.pause();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPlaying]);

  return (
    <AudioPlayerControlsComponent
      src={src}
      currentTime={currentTime}
      duration={duration}
      loop={loop}
      isLoading={isLoading}
      isPlaying={isPlaying}
      isPlaylistMode={isPlaylistMode}
      isError={isError}
      isPlayerExpandable={isPlayerExpandable}
      handleRewind={handleRewind}
      handleSkip={queue.length > 1 ? handleSkip : undefined}
      onPlayPause={playPauseAudio}
      toggleLoop={toggleLoop}
      playbackEngineType={playbackEngine.type}
      onSeek={playbackEngine.type === 'midi' ? handleSeek : undefined}
      noteDensity={playbackEngine.getNoteDensity()}
    />
  );
};
